"use client";

import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Label } from "@/app/components/ui/label";
import { QuantitySlider } from "@/app/components/ui/quantity-slider";
import { Table, RotateCcw, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { CopyButton } from "@/app/components/ui/copy-button";

export default function MarkdownTableGeneratorTool() {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [headers, setHeaders] = useState<string[]>([]);
  const [data, setData] = useState<string[][]>([]);
  const [generatedTable, setGeneratedTable] = useState("");

  const generateTable = () => {
    if (rows < 1 || cols < 1) {
      toast.error("Rows and columns must be at least 1");
      return;
    }

    let table = "";
    
    // Generate headers
    const headerRow = [];
    for (let i = 0; i < cols; i++) {
      headerRow.push(`Header ${i + 1}`);
    }
    table += "| " + headerRow.join(" | ") + " |\n";
    
    // Generate separator row
    const separatorRow = [];
    for (let i = 0; i < cols; i++) {
      separatorRow.push("---");
    }
    table += "| " + separatorRow.join(" | ") + " |\n";
    
    // Generate data rows
    for (let i = 0; i < rows; i++) {
      const dataRow = [];
      for (let j = 0; j < cols; j++) {
        dataRow.push(`Cell ${i + 1},${j + 1}`);
      }
      table += "| " + dataRow.join(" | ") + " |\n";
    }
    
    setGeneratedTable(table);
    toast.success("Markdown table generated successfully!");
  };

  const clearAll = () => {
    setGeneratedTable("");
    toast.info("Table cleared");
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Table className="h-5 w-5" />
              Markdown Table Generator
            </CardTitle>
            <CardDescription>
              Generate markdown tables for documentation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <QuantitySlider
                label="Number of Rows"
                value={rows}
                min={1}
                max={20}
                step={1}
                onValueChange={setRows}
                hint="1–20 rows"
              />
              <QuantitySlider
                label="Number of Columns"
                value={cols}
                min={1}
                max={10}
                step={1}
                onValueChange={setCols}
                hint="1–10 columns"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={generateTable}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Generate Table
              </Button>
              <Button onClick={clearAll} variant="outline">
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>Generated Table</CardTitle>
            <CardDescription>
              Your markdown table will appear here
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {generatedTable ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Markdown Code</Label>
                  <pre className="p-4 bg-muted/50 rounded text-sm overflow-x-auto">
                    {generatedTable}
                  </pre>
                </div>
                
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="p-4 bg-muted/50 rounded">
                    <div className="prose prose-sm max-w-none">
                      <table className="table-auto border-collapse border border-gray-300">
                        <thead>
                          <tr>
                            {Array.from({ length: cols }, (_, i) => (
                              <th key={i} className="border border-gray-300 px-2 py-1 bg-gray-100">
                                Header {i + 1}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Array.from({ length: rows }, (_, i) => (
                            <tr key={i}>
                              {Array.from({ length: cols }, (_, j) => (
                                <td key={j} className="border border-gray-300 px-2 py-1">
                                  Cell {i + 1},{j + 1}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Table className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Click "Generate Table" to see your markdown table here</p>
              </div>
            )}

            {generatedTable && (
              <CopyButton
                text={generatedTable}
                variant="default"
                size="default"
                className="w-full"
                successMessage="Table copied!"
              >
                Copy Table
              </CopyButton>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>Markdown Table Features</CardTitle>
          <CardDescription>
            Generate tables for documentation and markdown files
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Customizable</div>
              <div className="text-sm text-muted-foreground">Set any number of rows and columns</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Markdown Ready</div>
              <div className="text-sm text-muted-foreground">Copy-paste into any markdown file</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Live Preview</div>
              <div className="text-sm text-muted-foreground">See how your table will look</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
